import { createContext, useContext, useEffect, useState } from "react";
import { createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword, signOut, signInWithPopup } from 'firebase/auth';
import { setDoc, doc, getDoc, serverTimestamp, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";
import { isCpfTaken, maskCPF, validateCPF } from "../utils/functions";
import GMTLoading from "../components/core/gmtLoading";

// Resultado das operacoes de autenticacao: alem de sucesso/falha, devolve o codigo de erro
// do Firebase Auth para que a tela possa mostrar uma mensagem especifica (ver getAuthErrorMessage).
export interface IAuthResult {
    success: boolean;
    errorCode: string | null;
}

interface AuthContextType {
    user: IUser | null;
    userId: string | null;
    setUser: React.Dispatch<React.SetStateAction<IUser | null>>;
    signUp: (name: string, phoneNumber: string, email: string, password: string) => Promise<IAuthResult>;
    logIn: (email: string, password: string) => Promise<IAuthResult>;
    logOut: () => Promise<void>;
    googleLogIn: () => Promise<null | undefined>;
    updateUserPhone: (phone: string) => Promise<boolean | null>;
    updateUserContact: (field: string, value: string) => Promise<boolean | null>;
    setUserPhoneVerified: () => Promise<boolean | null>;
    setUserEmailVerified: () => Promise<boolean | null>;
    setTermsAccepted: (version: string) => Promise<boolean | null>;
    setAdvertiserTermsAccepted: (version: string) => Promise<boolean | null>;
    getTerm: (name: string) => Promise<{} | null>;
    updateUserCpf: (cpf: string) => Promise<CpfUpdateResult>;
}

// Resultado de updateUserCpf: a tela precisa distinguir "CPF de outra pessoa"
// de uma falha genérica para dar a instrução certa ao usuário.
export type CpfUpdateResult = "ok" | "invalid" | "duplicate" | "already-set" | "error";

export interface IUser {
    name: string;
    cpf?: string;
    phoneNumber: string | null;
    email: string;
    description?: string;
    instagram?: string;
    facebook?: string;
    site?: string;
    slug?: string;
    roles: [string];
    phoneVerified?: boolean;
    emailVerified?: boolean;
    termsAccepted?: {
        accepted: boolean;
        acceptedAt: string;
        version: string;
    };
    advertiserTermsAccepted?: {
        accepted: boolean;
        acceptedAt: string;
        version: string;
    };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {

    const [user, setUser] = useState<IUser | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [pending, setPending] = useState(true);

    useEffect(() => {
        auth.onAuthStateChanged((u) => {
            const uid = u?.uid;
            if (uid) {
                setUserId(uid);
                getUserAdditionalData(uid);
            } else {
                setPending(false);
            }
        })
    }, [])

    async function getUserAdditionalData(uid: string) {
        // const usersCollection = collection(db, "users");
        // const q = query(usersCollection, where("userId", "==", uid))
        // const usersDocs = await getDocs(q);
        const docRef = doc(db, "users", uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const userData = docSnap.data();
            // backfill: se o Auth já reporta e-mail verificado mas o documento ainda não
            // reflete isso (conta antiga, ou primeira vez logando após esta mudança), persiste agora
            const emailVerifiedNow = !!auth.currentUser?.emailVerified;
            if (emailVerifiedNow && !userData.emailVerified) {
                updateDoc(docRef, { emailVerified: true }).catch(error => console.log(error));
            }
            setUser({
                name: userData.name,
                email: userData.email,
                phoneNumber: userData.phoneNumber,
                roles: userData.roles,
                slug: userData.slug,
                instagram: userData.instagram,
                facebook: userData.facebook,
                site: userData.site,
                description: userData.description,
                phoneVerified: userData.phoneVerified,
                emailVerified: userData.emailVerified || emailVerifiedNow,
                termsAccepted: userData.termsAccepted,
                advertiserTermsAccepted: userData.advertiserTermsAccepted,
                cpf: userData.cpf
            })
        }
        setPending(false);
    }

    async function updateUserPhone(phone: string) {
        try {
            if (!userId || !user) {
                return null;
            }
            const docRef = doc(db, "users", userId);
            const docSnap = await getDoc(docRef);
            if (docSnap?.data()?.phoneVerified) {
                return null;
            }
            await updateDoc(docRef, { phoneNumber: phone, phoneVerified: false });
            setUser({ ...user, phoneNumber: phone, phoneVerified: false });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function updateUserCpf(cpf: string): Promise<CpfUpdateResult> {
        try {
            if (!userId || !user) {
                return "error";
            }
            if (!validateCPF(cpf)) {
                return "invalid";
            }
            const docRef = doc(db, "users", userId);
            const docSnap = await getDoc(docRef);
            if (docSnap?.data()?.cpf) {
                return "already-set";
            }
            // unicidade: dois usuários não podem ter o mesmo CPF (ver isCpfTaken)
            const taken = await isCpfTaken(cpf);
            // undefined = a consulta falhou: não dá para afirmar que é único,
            // então a gravação é abortada (fail closed)
            if (taken === undefined) {
                return "error";
            }
            if (taken) {
                return "duplicate";
            }
            const masked = maskCPF(cpf);
            await updateDoc(docRef, { cpf: masked });
            setUser({ ...user, cpf: masked });
            return "ok";
        } catch (error) {
            console.log(error);
            return "error";
        }
    }

    async function setUserPhoneVerified() {
        try {
            if (!userId || !user) {
                return null;
            }
            const docRef = doc(db, "users", userId);
            const docSnap = await getDoc(docRef);
            if (docSnap?.data()?.phoneVerified) {
                return null;
            }
            await updateDoc(docRef, { phoneVerified: true });
            setUser({ ...user, phoneVerified: true });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function setUserEmailVerified() {
        try {
            if (!userId || !user) {
                return null;
            }
            const docRef = doc(db, "users", userId);
            const docSnap = await getDoc(docRef);
            if (docSnap?.data()?.emailVerified) {
                return null;
            }
            await updateDoc(docRef, { emailVerified: true });
            setUser({ ...user, emailVerified: true });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function setTermsAccepted(version: string) {
        try {
            if (!userId || !user) {
                return null;
            }
            const docRef = doc(db, "users", userId);
            const docSnap = await getDoc(docRef);
            if (docSnap?.data()?.termsAccepted?.accepted && docSnap?.data()?.termsAccepted?.version === version) {
                return true;
            }
            await updateDoc(docRef, {
                termsAccepted: {
                    accepted: true,
                    acceptedAt: new Date().toISOString(),
                    version: version
                }
            });
            setUser({
                ...user,
                termsAccepted: {
                    accepted: true,
                    acceptedAt: new Date().toISOString(),
                    version: version
                }
            });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function setAdvertiserTermsAccepted(version: string) {
        try {
            if (!userId || !user || !user?.roles.includes("advertiser")) {
                return null;
            }
            const docRef = doc(db, "users", userId);
            const docSnap = await getDoc(docRef);
            if (docSnap?.data()?.advertiserTermsAccepted?.accepted && docSnap?.data()?.advertiserTermsAccepted?.version === version) {
                return true;
            }
            await updateDoc(docRef, {
                advertiserTermsAccepted: {
                    accepted: true,
                    acceptedAt: new Date().toISOString(),
                    version: version
                }
            });
            setUser({
                ...user,
                advertiserTermsAccepted: {
                    accepted: true,
                    acceptedAt: new Date().toISOString(),
                    version: version
                }
            });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function updateUserContact(field: string, value: string) {
        try {
            if (!userId || !user) {
                return null;
            }
            if (field !== "instagram" && field !== "facebook" && field !== "site" && field !== "description") {
                return null
            }
            const docRef = doc(db, "users", userId);
            let updateFields: { [key: string]: string } = {};
            updateFields[field] = value;
            await updateDoc(docRef, updateFields);
            setUser({ ...user, ...updateFields });
            return true;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    async function signUp(name: string, phoneNumber: string, email: string, password: string) {
        try {
            // criar usuario
            const user = await createUserWithEmailAndPassword(auth, email, password);
            const userData = user.user;

            await updateProfile(userData, {
                displayName: name
            })

            // salvar dados adicionais
            await setDoc(doc(db, "users", userData.uid), {
                name: name,
                email: email,
                phoneNumber: phoneNumber,
                roles: ["customer"],
                createdAt: serverTimestamp()
            });
            // colocar user no context
            setUser({ name, phoneNumber, email, roles: ["customer"] });
            setUserId(userData.uid);
            return { success: true, errorCode: null };
        } catch (error: any) {
            console.log(error);
            return { success: false, errorCode: error?.code || null };
        }
    }

    async function logIn(email: string, password: string) {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            return { success: true, errorCode: null };
        } catch (error: any) {
            console.log(error);
            return { success: false, errorCode: error?.code || null };
        }
    }

    async function logOut() {
        try {
            await signOut(auth);
            setUser(null);
            window.location.reload();
        } catch (error) {
            console.log(error);
        }
    }

    async function googleLogIn() {
        try {
            const user = await signInWithPopup(auth, googleProvider);
            const userData = user.user;

            const existantUser = await getDoc(doc(db, "users", userData.uid));
            if (existantUser.exists()) {
                return null;
            }
            // salvar dados adicionais
            await setDoc(doc(db, "users", userData.uid), {
                name: userData.displayName,
                email: userData.email,
                phoneNumber: userData.phoneNumber,
                roles: ["customer"],
                createdAt: serverTimestamp()
            });
            // // colocar user no context
            if (userData.displayName && userData.email) {
                setUser({ name: userData.displayName, phoneNumber: userData.phoneNumber, email: userData.email, roles: ["customer"] });
                setUserId(userData.uid);
            }
        } catch (error) {
            console.log(error);
        }
    }

    async function getTerm(name: string) {
        try {
            let docs = {}
            const q = query(collection(db, "terms"), where("name", "==", name));
            const docSnap = await getDocs(q);
            if (docSnap.empty) {
                return null;
            }

            docSnap.forEach(document => {
                docs = {
                    version: document.data().version,
                    name: document.data().name,
                    term: document.data().term,
                    title: document.data().title,
                    updatedAt: document.data().updatedAt
                }
            })
            return docs;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    return (
        <AuthContext.Provider value={
            {
                user, setUser, userId,
                signUp, logIn, logOut,
                googleLogIn, updateUserPhone,
                updateUserContact, setUserPhoneVerified,
                setUserEmailVerified,
                setTermsAccepted, setAdvertiserTermsAccepted,
                getTerm, updateUserCpf
            }
        }>
            {
                pending ?
                    <GMTLoading />
                    : children
            }
        </AuthContext.Provider>
    )
}

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
